<script lang="ts" setup>
import { onMounted, ref, onUpdated } from 'vue';

import {
  useBrowserStore,
  usePriceCalendarStore,
} from '@apps/main/new-desktop/core/stores';
import type { EditTripSegment } from '@apps/main/new-desktop/modules/editTripModal';
import {
  EditTripStepType,
  EditTripView,
} from '@apps/main/new-desktop/modules/editTripModal';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';
import type {
  AddFlightsModalInitialRoute,
  AddFligthsModalRoute,
} from '@apps/main/new-desktop/modules/addFlightsModal';
import { AddFlightsView } from '@apps/main/new-desktop/modules/addFlightsModal';
import { PriceCalendar } from '@apps/main/new-desktop/modules/priceCalendar';
import { PaxTab } from '@apps/main/new-desktop/modules/paxSelector';
import { useConfigurationPanelStore } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore';
import { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';

import { AppSlidePanel, AppTabs } from '@libs/ui';
import type {
  AutoCompleteSuggestion,
  IngredientId,
  Pax,
} from '@libs/trip-planner-interfaces';

import { useTripOverviewPageStore } from '../../stores';

interface ConfigurationPanelProps {
  isOpen: boolean;
}

const props = defineProps<ConfigurationPanelProps>();

interface ConfigurationPanelEmits {
  (eventName: 'close'): void;
  (eventName: 'confirm'): void;
  (eventName: 'update-pax', newPax: Pax): void;
  (eventName: 'add-destination', suggestion: AutoCompleteSuggestion): void;
  (
    eventName: 'change-destination',
    {
      suggestion,
      ingredientId,
    }: { suggestion: AutoCompleteSuggestion; ingredientId: IngredientId }
  ): void;
  (eventName: 'change-trip-order', segments: EditTripSegment[]): void;
  (eventName: 'change-number-of-nights', segment: EditTripSegment): void;
  (eventName: 'remove-destination', segment: EditTripSegment): void;
}
const emit = defineEmits<ConfigurationPanelEmits>();

const configurationPanelStore = useConfigurationPanelStore();
const priceCalendarStore = usePriceCalendarStore();
const browserStore = useBrowserStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const mapStore = useMapStore();

const newRoutes = ref<AddFligthsModalRoute[]>([]);
const mapRoutes = ref<AddFlightsModalInitialRoute[]>([]);

function onClose(): void {
  emit('close');
}

function onConfirm(): void {
  if (
    configurationPanelStore.isEditing ||
    configurationPanelStore.editTripDetails?.initialStep.stepType !==
      EditTripStepType.Overview
  ) {
    configurationPanelStore.changeEditStep({
      stepType: EditTripStepType.Overview,
    });
    configurationPanelStore.updateEditing(false);
    return;
  }
  if (configurationPanelStore.getTabSelected === TabKey.Airplane) {
    tripOverviewPageStore.confirmAddFlightsModal(newRoutes.value);
    onClose();
  } else {
    onClose();
  }
}

function onSelectMonth(dayInMonth: Date): void {
  priceCalendarStore.selectItineraryMonth(
    dayInMonth,
    CoreEventSource.MonthsSelector
  );
}

function onSelectDate(date: Date): void {
  priceCalendarStore.selectItineraryDate(date, CoreEventSource.MonthsSelector);
}

function onChangeTab(tabKey: string): void {
  const key = Object.values(TabKey).find((key) => key === tabKey);
  if (key && !configurationPanelStore.isEditing) {
    configurationPanelStore.handleTabSwitch(key);
  }
}

function onUpdateRoutes(routes: AddFligthsModalRoute[]): void {
  newRoutes.value = routes;
}

onMounted(() => {
  mapRoutes.value = tripOverviewPageStore.getAddFlightsModalRoutes();
});
onUpdated(() => {
  mapRoutes.value = tripOverviewPageStore.getAddFlightsModalRoutes();
  if (props.isOpen && !browserStore.isLargerThan1199px) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});
</script>

<template>
  <AppSlidePanel
    :disable-header="true"
    direction="right"
    :is-open="props.isOpen"
    :width="!browserStore.isLargerThan1199px ? '100%' : '33.33%'"
    padding="0"
    should-show-close-button
    :close-button-aria-label="$t('a11y.close_panel')"
    @close="onClose"
    @confirm="onConfirm"
  >
    <template #body>
      <div
        class="configuration-panel-body-wrapper"
        :class="{
          'no-gap': configurationPanelStore.getTabSelected === TabKey.Calendar,
        }"
      >
        <div class="configuration-panel-tabs-wrapper">
          <div class="configuration-panel-tab-selector-wrapper">
            <AppTabs
              :tabs="
                configurationPanelStore.getConfigurationPanelTabs(
                  tripOverviewPageStore.homeFlightData != null
                )
              "
              :selected-tab-key="configurationPanelStore.getTabSelected"
              @tab-click="onChangeTab"
            />
          </div>
        </div>
        <div
          class="tabs-content"
          :class="{
            'no-padding':
              configurationPanelStore.getTabSelected === TabKey.Calendar,
          }"
        >
          <PaxTab
            v-if="
              configurationPanelStore.getPaxDetails &&
              configurationPanelStore.getTabSelected === TabKey.Pax
            "
            :pax-details="configurationPanelStore.getPaxDetails"
            @update-pax="emit('update-pax', $event)"
          />
          <template
            v-if="configurationPanelStore.getTabSelected === TabKey.Calendar"
          >
            <PriceCalendar
              :selected-month="priceCalendarStore.selectedMonthOption.id"
              :months="priceCalendarStore.priceRibbonMonths"
              :price-calendar-state="priceCalendarStore.priceCalendarState"
              :show-prices="true"
              :allow-hover-states="true"
              :show-header-padding="true"
              class="price-calendar"
              @select-date="onSelectDate"
              @select-month="onSelectMonth"
            />
          </template>
          <EditTripView
            v-if="
              configurationPanelStore.editTripDetails &&
              configurationPanelStore.getTabSelected === TabKey.Itinerary
            "
            :segments="configurationPanelStore.editTripDetails.segments"
            :is-desktop="configurationPanelStore.editTripDetails.isDesktop"
            :initial-step="configurationPanelStore.editTripDetails.initialStep"
            @add-destination="emit('add-destination', $event)"
            @change-destination="emit('change-destination', $event)"
            @change-trip-order="emit('change-trip-order', $event)"
            @change-number-of-nights="emit('change-number-of-nights', $event)"
            @remove-destination="emit('remove-destination', $event)"
            @change-step="configurationPanelStore.changeEditStep"
            @close="emit('close')"
          />
          <AddFlightsView
            v-if="configurationPanelStore.getTabSelected === TabKey.Airplane"
            :routes="mapRoutes"
            :start-positon="mapStore.startingLocation"
            :end-position="mapStore.endLocation"
            :is-editing="configurationPanelStore.isEditing"
            @routes="onUpdateRoutes"
            @is-editing="configurationPanelStore.updateEditing($event)"
          />
        </div>
      </div>
    </template>
    <template #button>
      {{
        browserStore.isSmallerThan1200px
          ? $t('planner.show_trip')
          : $t('planner.done')
      }}
    </template>
  </AppSlidePanel>
</template>

<style lang="scss" scoped>
.configuration-panel-body-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-40);
  height: 100%;

  &.no-gap {
    gap: 0;
  }

  .configuration-panel-tabs-wrapper {
    display: flex;
    width: 100%;

    @include for-more-or-equal-than-599-px {
      padding-top: var(--spacing-24);
    }
    .configuration-panel-tab-selector-wrapper {
      position: relative;
      width: 100%;
      margin: 0 var(--spacing-24);
      @include for-less-or-equal-than-1199-px {
        margin: 0;
      }
    }
  }

  .tabs-content {
    padding: 0 var(--spacing-24);
    height: 100%;

    &.no-padding {
      padding: 0;
    }
  }
}
</style>
